import MoRunning from '@assets/animations/mo/moRunning.json';
import MoWaving from '@assets/animations/mo/moWaving.json';
import {Typography} from '@mui/material';
import {Box} from '@mui/system';
import {motion} from 'framer-motion';
import Lottie from 'lottie-react';
import {useTranslation} from 'react-i18next';

import {MessageBubble} from '@/app/shared-components';
import {
  EMessageBubbleSizeVariant,
  EMessageBubbleTailVariant,
  EMessageBubbleToneVariant,
} from '@/app/shared-components/MessageBubble/enums';
import {CSpinner} from '@/constants/SpinnerConstants';

import {KYC_SUBMITTED_ANIMATION} from './constants';
import {
  KYC_SUBMITTED_PHASE,
  KycSubmittedViewModelProvider,
  useKycSubmittedViewModelProvider,
} from './ViewModelContext';

import './styles';

const KycSubmittedContent = () => {
  const {t} = useTranslation();
  const {phase, surveyOptionIds, selectedSurveyOption, onSelectSurveyOption} =
    useKycSubmittedViewModelProvider();

  return (
    <Box className="kyc-submitted-page">
      <Box
        className={`kyc-submitted-content ${
          phase === KYC_SUBMITTED_PHASE.WAVING ||
          phase === KYC_SUBMITTED_PHASE.LONG_WAIT
            ? 'waving-phase'
            : ''
        }`}>
        {/* Loader Container */}
        <Box className="loader-container">
          <Box
            component="img"
            src="/assets/images/logo/plaid_circle_logo.svg"
            alt="Plaid Logo"
            className="loader-logo"
          />

          <Lottie
            className="mdk-lottie"
            animationData={CSpinner.ELLIPSE_PATH}
            loop
            autoplay
          />

          <Box
            component="img"
            src="/assets/images/logo/modak-circle-logo.svg"
            alt="Modak Logo"
            className="loader-logo"
          />
        </Box>

        {/* Title */}
        <Typography className="kyc-submitted-title">
          {t('kyc.kyc_submitted.title')}
        </Typography>
      </Box>

      {phase === KYC_SUBMITTED_PHASE.SURVEY && (
        <motion.div
          className="kyc-submitted-survey-container"
          initial={{opacity: 0}}
          animate={{opacity: 1}}
          transition={{
            duration: KYC_SUBMITTED_ANIMATION.SURVEY_CONTAINER_FADE_DURATION,
          }}>
          <Typography className="kyc-submitted-survey-title">
            {t('kyc.kyc_submitted.survey.question')}
          </Typography>
          <Box className="kyc-submitted-survey-options">
            {surveyOptionIds.map((optionId, index) => {
              const isSelected = selectedSurveyOption === optionId;
              const surveyOptionDuration =
                KYC_SUBMITTED_ANIMATION.SURVEY_OPTION_FADE_DURATION;
              const surveyOptionDelay =
                KYC_SUBMITTED_ANIMATION.SURVEY_OPTION_BASE_DELAY +
                index * KYC_SUBMITTED_ANIMATION.SURVEY_OPTION_STAGGER_DELAY;
              return (
                <motion.div
                  key={optionId}
                  initial={{
                    opacity: 0,
                    x: KYC_SUBMITTED_ANIMATION.SURVEY_OPTION_INITIAL_X,
                  }}
                  animate={{opacity: 1, x: 0}}
                  transition={{
                    duration: surveyOptionDuration,
                    delay: surveyOptionDelay,
                  }}
                  className={`kyc-submitted-survey-option ${
                    isSelected ? 'selected' : ''
                  }`}
                  onClick={() => onSelectSurveyOption(optionId)}
                  role="radio"
                  aria-checked={isSelected}
                  tabIndex={0}
                  onKeyDown={event => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      onSelectSurveyOption(optionId);
                    }
                  }}>
                  <Box className="kyc-submitted-survey-option-radio" />
                  <Typography className="kyc-submitted-survey-option-label">
                    {t(`kyc.kyc_submitted.survey.options.${optionId}`)}
                  </Typography>
                </motion.div>
              );
            })}
          </Box>
        </motion.div>
      )}

      {phase === KYC_SUBMITTED_PHASE.WAVING && (
        <Box className="kyc-submitted-thankyou-container">
          <motion.div
            className="kyc-submitted-thankyou-lottie-motion"
            initial={{
              opacity: 0,
              y: KYC_SUBMITTED_ANIMATION.THANKYOU_LOTTIE_INITIAL_Y,
            }}
            animate={{opacity: 1, y: 0}}
            transition={{
              duration: KYC_SUBMITTED_ANIMATION.THANKYOU_LOTTIE_FADE_DURATION,
              ease: 'easeOut',
            }}>
            <Lottie
              className="kyc-submitted-thankyou-lottie"
              animationData={MoWaving}
              loop
              autoplay
            />
          </motion.div>
          <motion.div
            className="kyc-submitted-thankyou-message-motion"
            initial={{opacity: 0}}
            animate={{opacity: 1}}
            transition={{
              duration: KYC_SUBMITTED_ANIMATION.THANKYOU_MESSAGE_FADE_DURATION,
              delay: KYC_SUBMITTED_ANIMATION.THANKYOU_MESSAGE_FADE_DELAY,
              ease: 'easeOut',
            }}>
            <MessageBubble
              text={t('kyc.kyc_submitted.survey.thank_you')}
              className="kyc-submitted-thankyou-message"
              tailVariant={EMessageBubbleTailVariant.Triangle}
              sizeVariant={EMessageBubbleSizeVariant.Md}
              toneVariant={EMessageBubbleToneVariant.Elevated}
            />
          </motion.div>
        </Box>
      )}

      {phase === KYC_SUBMITTED_PHASE.LONG_WAIT && (
        <Box className="kyc-submitted-thankyou-container">
          <motion.div
            className="kyc-submitted-thankyou-lottie-motion"
            initial={{
              opacity: 0,
              y: KYC_SUBMITTED_ANIMATION.THANKYOU_LOTTIE_INITIAL_Y,
            }}
            animate={{opacity: 1, y: 0}}
            transition={{
              duration: KYC_SUBMITTED_ANIMATION.THANKYOU_LOTTIE_FADE_DURATION,
              ease: 'easeOut',
            }}>
            <Lottie
              className="kyc-submitted-thankyou-lottie"
              animationData={MoRunning}
              loop
              autoplay
            />
          </motion.div>
          <motion.div
            className="kyc-submitted-thankyou-message-motion"
            initial={{opacity: 0}}
            animate={{opacity: 1}}
            transition={{
              duration: KYC_SUBMITTED_ANIMATION.THANKYOU_MESSAGE_FADE_DURATION,
              delay: KYC_SUBMITTED_ANIMATION.THANKYOU_MESSAGE_FADE_DELAY,
              ease: 'easeOut',
            }}>
            <MessageBubble
              text={t('kyc.kyc_submitted.long_wait_message')}
              className="kyc-submitted-thankyou-message"
              tailVariant={EMessageBubbleTailVariant.Triangle}
              sizeVariant={EMessageBubbleSizeVariant.Md}
              toneVariant={EMessageBubbleToneVariant.Elevated}
            />
          </motion.div>
        </Box>
      )}
    </Box>
  );
};

const KycSubmittedPage = () => (
  <KycSubmittedViewModelProvider>
    <KycSubmittedContent />
  </KycSubmittedViewModelProvider>
);

export default KycSubmittedPage;
